import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Box,
    CircularProgress,
    Grid,
    Tooltip,
    IconButton,
    FormHelperText,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    TextField,
} from '@material-ui/core';
import { Warning as WarningIcon } from '@material-ui/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { sentenceCase } from 'sentence-case';
import { colorForStatus, orderStatuses } from '../new-order/helpers/statuses';

// styles
import useStyles from './styles';

// components
import { Button, Typography, Chip } from '../../components/Wrappers/Wrappers';
import Widget from '../../components/Widget/Widget';
import TagSelect from '../new-order/components/TagSelect';

// context
import { useOrderDispatch, useOrderState, actions as orderActions } from '../../context/OrderContext';
import { usePracticeDispatch, usePracticeState, actions as practiceActions } from '../../context/PracticeContext';

// helpers
import { copyToClipboard, getMethodLabel, getPracticeName } from '../../common/helpers';
import { mainComplaintsOptions } from '../new-order/helpers/form';

// config
import config from '../../config';
import { apiGet } from '../../common/amplifyApi';

/**
 * List of all teeth identifiers used in the system.
 */
const TEETH_ALL = [
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '21',
    '22',
    '23',
    '24',
    '25',
    '26',
    '27',
    '28',
    '31',
    '32',
    '33',
    '34',
    '35',
    '36',
    '37',
    '38',
    '41',
    '42',
    '43',
    '44',
    '45',
    '46',
    '47',
    '48',
    '51',
    '52',
    '53',
    '54',
    '55',
    '61',
    '62',
    '63',
    '64',
    '65',
    '71',
    '72',
    '73',
    '74',
    '75',
    '81',
    '82',
    '83',
    '84',
    '85',
];

const ViewOrder = () => {
    const classes = useStyles();

    const orderDispatch = useOrderDispatch();
    const orderState = useOrderState();

    const practiceDispatch = usePracticeDispatch();
    const practiceState = usePracticeState();

    const history = useHistory();
    const location = useLocation();

    const [isCancelOrderModalOpen, setCancelOrderModalOpen] = useState(false);
    const [isCopyingPatientLink, setCopyingPatientLink] = useState(false);
    // Set when the link was fetched but the browser refused the clipboard write, so it can be copied manually
    const [patientLinkToCopy, setPatientLinkToCopy] = useState(null);

    useEffect(() => {
        if (!practiceState.practices) practiceActions.doFetch()(practiceDispatch);
    }, []); // eslint-disable-line

    useEffect(() => {
        const queryString = new URLSearchParams(location.search);
        const practiceId = queryString.get('practiceId');
        const orderId = queryString.get('orderId');
        if (!practiceId || !orderId) {
            toast.error('Unable to find practice id or order id');
            setTimeout(() => backToList(), 2000);
        } else if (!orderState.currentOrder || orderState.currentOrder.orderId !== orderId) {
            orderActions.doFind(practiceId, orderId)(orderDispatch);
        }
    }, [location.search]); //eslint-disable-line

    // Refetch order after an update to ensure we have the latest data
    useEffect(() => {
        if (orderState.orderNeedsRefresh && orderState.currentOrder) {
            const { practiceId, orderId } = orderState.currentOrder;
            orderDispatch({ type: 'ORDER_REFRESH_COMPLETE' });
            orderActions.doFind(practiceId, orderId)(orderDispatch);
        }
    }, [orderState.orderNeedsRefresh]); //eslint-disable-line

    useEffect(() => {
        if (!orderState.findError) return;
        setTimeout(() => backToList(), 2000);
    }, [orderState?.findError]); //eslint-disable-line

    const handleOpenLink = (url) => (_) => window.open(url, '_blank');

    const handleCopyToken = (token) => (_) => navigator.clipboard.writeText(token);

    const handleDownloadFile = (filekey) => (_) => orderActions.doDownloadFile(filekey);

    const handleCopyPatientLinkFromDialog = async () => {
        try {
            await navigator.clipboard.writeText(patientLinkToCopy);
            toast.success('Patient link copied to clipboard');
            setPatientLinkToCopy(null);
        } catch (err) {
            console.error('Error copying patient link:', err);
            toast.error('Unable to copy automatically. Select the link and copy it manually.');
        }
    };

    const handleCancelOrder = () => setCancelOrderModalOpen(true);

    const handleCompleteOrder = () => {
        const { orderId, practiceId } = orderState.currentOrder;
        orderActions.doComplete(orderId, practiceId)(orderDispatch, history);
    };

    const handleOrderRetainers = () => {
        const data = {
            practiceId: orderState.currentOrder.practiceId,
            retainersDelivered: 'Yes',
            retainersDeliveredOn: new Date().toISOString().split('T')[0], // Format as YYYY-MM-DD
        };
        orderActions.doUpdate(orderState.currentOrder.orderId, data)(orderDispatch);
    };

    const backToList = () => history.goBack();

    const handleClickEdit = () => {
        orderActions.doInitUpdate()(orderDispatch);
        const { orderId, practiceId } = orderState.currentOrder;
        history.push(`edit?practiceId=${practiceId}&orderId=${orderId}`);
    };

    const handleClickCreateRefinement = () => {
        const { orderId, practiceId, orderType } = orderState.currentOrder;
        if (orderType === 'nova') {
            history.push(`/app/nova-order/refine?practiceId=${practiceId}&orderId=${orderId}`);
        } else {
            history.push(`refine?practiceId=${practiceId}&orderId=${orderId}`);
        }
    };

    const handleClickViewRefinement = () => {
        const { practiceId, refinementOrderId } = orderState.currentOrder;
        history.push(`view?practiceId=${practiceId}&orderId=${refinementOrderId}`);
    };

    const handleClickViewSourceOrder = () => {
        const { practiceId, previousOrderId } = orderState.currentOrder;
        history.push(`view?practiceId=${practiceId}&orderId=${previousOrderId}`);
    };

    const handleModalAction = (action) => () => {
        setCancelOrderModalOpen(false);
        if (action === 'Confirm') {
            const { orderId, practiceId } = orderState.currentOrder;
            orderActions.doCancel(orderId, practiceId)(orderDispatch, history);
        }
    };

    // Helper function to get display label for property keys
    const getPropertyLabel = (key) => {
        if (key.match('IPR')) return sentenceCase(key).replace('ipr', 'IPR');
        if (key.match('practiceId')) return 'Practice';
        if (key === 'createdAt') return 'Order Created At';
        if (key === 'fcOrderStatus') return '3Shape Status';
        if (key === 'patientDOB') return 'Patient DOB';
        if (key === 'mainComplaints') return 'Patient Concerns';
        if (key === 'allowTADs') return 'Allow TADs';
        if (key === 'photosURLs') return 'Photos URLs';
        if (key === 'maxillaryArchURL') return 'Upper Arch';
        if (key === 'mandibularArchURL') return 'Lower Arch';
        if (key === 'caseId') return 'Case ID';
        if (key === 'upperStepsCount') return 'Upper Steps Count';
        if (key === 'lowerStepsCount') return 'Lower Steps Count';
        if (key === 'previousOrderId') return 'Source Order';
        if (key === 'refinementOrderId') return 'Refinement Order';
        if (key === 'refinementIssueDescription') return 'Refinement Issue Description';
        if (key === 'refinementProblemTeeth') return 'Refinement Problem Teeth';
        if (key === 'lastUpperAlignerWorn') return 'Last Upper Aligner Worn';
        if (key === 'lastLowerAlignerWorn') return 'Last Lower Aligner Worn';
        if (key === 'includeUpperArchRetainer') return 'Include Upper Arch Retainer';
        if (key === 'includeLowerArchRetainer') return 'Include Lower Arch Retainer';
        return sentenceCase(key);
    };

    // Helper function to render property value based on key type
    const renderPropertyValue = (key, value, order) => {
        const orderStatus = order?.orderStatus;
        const canCancelOrder = orderStatus === orderStatuses.CASE_NEEDS_APPROVAL;
        const canCompleteOrder = orderStatus === orderStatuses.CASE_IN_PRODUCTION;
        const canCreateRefinement = orderStatus === orderStatuses.CASE_COMPLETE && !order?.refinementOrderId;
        const canDeliverRetainers = order.retainersDelivered === 'No';

        if (key === 'orderStatus') {
            return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                    <Chip label={value} color={colorForStatus(value)} />
                    {orderState.currentOrder?.orderStatus === orderStatuses.CASE_NEEDS_APPROVAL &&
                        orderState.currentOrder?.treatmentInvoiced && (
                            <Tooltip
                                title={
                                    <Typography fontSize={30}>
                                        Case pending for over 2 months. Treatment plan only has been invoiced and will
                                        be deducted from total course cost when case is approved.
                                    </Typography>
                                }
                            >
                                <IconButton>
                                    <WarningIcon color="primary" />
                                </IconButton>
                            </Tooltip>
                        )}

                    {canCancelOrder && (
                        <Button onClick={handleCancelOrder} variant="contained" color="secondary">
                            Cancel Order
                        </Button>
                    )}

                    {canCompleteOrder && (
                        <Button onClick={handleCompleteOrder} variant="contained" color="secondary">
                            Complete Order
                        </Button>
                    )}

                    {canCreateRefinement && (
                        <Button onClick={handleClickCreateRefinement} variant="contained" color="secondary">
                            Create Refinement
                        </Button>
                    )}
                </div>
            );
        }

        if (key === 'practiceId') {
            return (
                <Typography className={classes.text}>
                    {getPracticeName(practiceState.practices?.items || [], value)}
                </Typography>
            );
        }

        if (key === 'method') {
            return <Typography className={classes.text}>{getMethodLabel(value)}</Typography>;
        }

        if (key === 'patientDOB') {
            return (
                <Typography className={classes.text}>
                    {new Date(value).toLocaleDateString('en-AU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                    })}
                </Typography>
            );
        }

        if (key === 'createdAt') {
            return (
                <Typography className={classes.text}>
                    {new Date(value).toLocaleDateString('en-AU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                    }) +
                        ' ' +
                        new Date(value)
                            .toLocaleTimeString('en-AU', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                            })
                            .toLowerCase()}
                </Typography>
            );
        }

        if (key === 'mainComplaints') {
            return (
                <TagSelect
                    id="mainComplaints-display"
                    label=""
                    options={mainComplaintsOptions}
                    value={Array.isArray(value) ? value : []}
                    readOnly={true}
                />
            );
        }

        if (key === 'photosURLs') {
            return (
                <Box>
                    {Array.isArray(value) && value.length > 0 ? (
                        value.map((url, index) => {
                            const fileName = url.split('/').pop() || `Attachment ${index + 1}`;
                            return (
                                <span key={index}>
                                    <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            color: '#3f51b5',
                                            textDecoration: 'underline',
                                        }}
                                    >
                                        {fileName}
                                    </a>
                                    {index < value.length - 1 && ', '}
                                </span>
                            );
                        })
                    ) : (
                        <Typography className={classes.text}>No attachments</Typography>
                    )}
                </Box>
            );
        }

        if (key === 'maxillaryArchURL' || key === 'mandibularArchURL') {
            return (
                <Box>
                    <Box key={key} mb={1}>
                        <a
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: '#3f51b5',
                                textDecoration: 'underline',
                                display: 'block',
                            }}
                        >
                            {key === 'maxillaryArchURL' ? 'Upper Arch Model' : 'Lower Arch Model'}
                        </a>
                    </Box>
                </Box>
            );
        }

        if (key === 'instructions' || key === 'comments') {
            return value.split('\n').map((item, idx) => (
                <span key={idx}>
                    <Typography className={classes.text}>{item}</Typography>
                </span>
            ));
        }

        if (key === 'retainersDelivered') {
            return canDeliverRetainers ? (
                <Box display="flex" alignItems="center">
                    <Typography className={classes.text}>{value}</Typography>
                    <Button
                        onClick={handleOrderRetainers}
                        variant="contained"
                        color="primary"
                        style={{ marginLeft: 25 }}
                    >
                        Order Free Retainers
                    </Button>
                </Box>
            ) : (
                <Typography className={classes.text}>{value}</Typography>
            );
        }

        if (key === 'retainersDeliveredOn') {
            // format date to DD/MM/YYYY
            return (
                <Typography className={classes.text}>
                    {new Date(value).toLocaleDateString('en-AU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                    })}
                </Typography>
            );
        }

        if (key === 'orderType') {
            return <Typography className={classes.text}>{value || '3Shape'}</Typography>;
        }

        if (key === 'previousOrderId') {
            return (
                <Button onClick={handleClickViewSourceOrder} variant="outlined" color="primary">
                    View Source Order
                </Button>
            );
        }

        if (key === 'refinementOrderId') {
            return (
                <Button onClick={handleClickViewRefinement} variant="outlined" color="secondary">
                    View Refinement
                </Button>
            );
        }

        if (key === 'refinementIssueDescription') {
            return value.split('\n').map((item, idx) => (
                <span key={idx}>
                    <Typography className={classes.text}>{item}</Typography>
                </span>
            ));
        }

        if (key === 'refinementProblemTeeth') {
            return (
                <TagSelect
                    id="refinementProblemTeeth-display"
                    label=""
                    options={TEETH_ALL}
                    value={Array.isArray(value) ? value : []}
                    readOnly={true}
                />
            );
        }

        if (key === 'lastUpperAlignerWorn' || key === 'lastLowerAlignerWorn') {
            return <Typography className={classes.text}>{value}</Typography>;
        }

        if (key === 'includeUpperArchRetainer' || key === 'includeLowerArchRetainer') {
            return <Typography className={classes.text}>{value}</Typography>;
        }

        return <Typography className={classes.text}>{value}</Typography>;
    };

    // Helper function to render a single property
    const renderProperty = (key, order) => {
        const value = order?.[key];

        // Skip if value is not defined or empty
        if (!value || (typeof value === 'string' && !value.length) || (Array.isArray(value) && value.length === 0))
            return null;

        return (
            <Grid item container key={key}>
                <Grid item xs={6}>
                    <Typography variant="body1" weight="bold">
                        {getPropertyLabel(key)}
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    {renderPropertyValue(key, value, order)}
                </Grid>
            </Grid>
        );
    };

    // Helper function to render a section with title and properties
    const renderSection = (title, properties, order) => {
        const sectionProperties = properties.map((property) => renderProperty(property, order)).filter(Boolean);

        if (sectionProperties.length === 0) return null;

        return (
            <Box key={title} mb={2} ml={1.5}>
                <Typography
                    variant="h6"
                    weight="medium"
                    style={{
                        marginBottom: 8,
                        color: '#F16567', // coral theme color
                        fontSize: '1.1rem',
                        fontWeight: 600,
                    }}
                >
                    {title}
                </Typography>
                <Divider style={{ marginBottom: 16, backgroundColor: '#F16567', height: 1.5 }} />
                <Grid container direction="column" spacing={2}>
                    {sectionProperties}
                </Grid>
            </Box>
        );
    };

    // Define section properties
    const orderInformationProperties = [
        'orderType',
        'provideTreatmentPlan',
        'orderStatus',
        'fcOrderStatus',
        'caseNumber',
        'serialNumber',
        'caseId',
        'manufacturer',
        'method',
        'createdAt',
    ];

    const refinementProperties = [
        'previousOrderId',
        'previousCaseNumber',
        'refinementOrderId',
        'refinementIssueDescription',
        'refinementProblemTeeth',
        'lastUpperAlignerWorn',
        'lastLowerAlignerWorn',
    ];

    const patientInformationProperties = ['patientName', 'patientGender', 'patientDOB'];

    const practiceDetailsProperties = [
        'prescriberName',
        'practiceId',
        'practiceEmail',
        'practicePhoneNumber',
        'treatmentInvoiced',
        'invoicingRecipient',
        'deliveryAddress',
        'territory',
    ];

    const caseDesignProperties = [
        'treatmentType',
        'mainComplaints',
        'arches',
        'includeUpperArchRetainer',
        'includeLowerArchRetainer',
        'allowIPR',
        'allowAttachment',
        'allowElastics',
        'allowTADs',
        'upperMidline',
        'lowerMidline',
        'attachmentTrayMaterial',
        'upperCrowding',
        'lowerCrowding',
        'posteriorCrossbite',
        'anteriorPosteriorRelation',
        'trimLine',
        'upperStepsCount',
        'lowerStepsCount',
        'retainersDelivered',
        'retainersDeliveredOn',
        'dentalMonitoring',
        'instructions',
        'comments',
        'reviewerNote',
    ];

    const caseReviewProperties = ['photosURLs', 'maxillaryArchURL', 'mandibularArchURL'];

    // Helper function to render special case review properties
    const renderCaseReviewSection = (order) => {
        const elements = [];

        // Render case links if available
        if (order?.links && Array.isArray(order.links) && order.links.length > 0) {
            elements.push(...order.links.map(renderCaseLink));
        }

        // Render Nova 3D viewer button for nova orders with planId
        const canReviewNovaOrder =
            order &&
            order.orderType === 'nova' &&
            order.planId &&
            (order.orderStatus === orderStatuses.CASE_NEEDS_APPROVAL ||
                order.orderStatus === orderStatuses.CASE_IN_PRODUCTION ||
                order.orderStatus === orderStatuses.CASE_COMPLETE);
        if (canReviewNovaOrder) {
            elements.push(renderNova3DViewerButton(order));
            elements.push(renderCopyPatientLinkButton(order));
        }

        // Render download actions for treatment plan files
        if (order?.treatmentPlanFile) {
            elements.push(renderDownloadAction('treatmentPlanFile', order.treatmentPlanFile));
        }

        // Render patient photos download
        if (order?.patientPhotos) {
            elements.push(renderDownloadAction('patientPhotos', order.patientPhotos));
        }

        // Render regular case review properties
        const regularProperties = caseReviewProperties
            .map((property) => renderProperty(property, order))
            .filter(Boolean);

        elements.push(...regularProperties);

        if (elements.length === 0) return null;

        return (
            <Box key="Case Review" mb={2} ml={1.5}>
                <Typography
                    variant="h6"
                    weight="medium"
                    style={{
                        marginBottom: 8,
                        color: '#F16567', // coral theme color
                        fontSize: '1.1rem',
                        fontWeight: 600,
                    }}
                >
                    Case Review
                </Typography>
                <Divider style={{ marginBottom: 16, backgroundColor: '#F16567', height: 2 }} />
                <Grid container direction="column" spacing={2}>
                    {elements}
                </Grid>
            </Box>
        );
    };

    // Render all sections
    const renderOrderSections = (order) => {
        if (!order) return null;

        const sections = [
            renderSection('Patient Information', patientInformationProperties, order),
            renderSection('Order Information', orderInformationProperties, order),
            renderSection('Refinement', refinementProperties, order),
            renderCaseReviewSection(order),
            renderSection('Case Design', caseDesignProperties, order),
            renderSection('Practice Details', practiceDetailsProperties, order),
        ];

        return sections.filter(Boolean);
    };

    const renderDownloadAction = (key, value) => (
        <Grid item container alignItems="center" key={key}>
            <Grid item xs={6}>
                <Typography variant="body1" weight="bold">
                    {sentenceCase(key)}
                </Typography>
            </Grid>
            <Grid item container xs={6} alignItems="center">
                <Button
                    className={classes.openLinkButton}
                    color="primary"
                    variant="contained"
                    onClick={handleDownloadFile(value)}
                >
                    Download
                </Button>
            </Grid>
        </Grid>
    );

    const renderNova3DViewerButton = (order) => {
        const handleOpenNova3D = () => {
            // Use React Router to navigate to the OpenPlan page in the same tab
            history.push(`/app/order/open-plan?orderId=${order.orderId}&planId=${order.planId}`);
        };

        const handleCopyDentistLink = () => {
            const url = `${config.myLabUrl}/#/app/order/open-plan?orderId=${order.orderId}&planId=${order.planId}`;
            navigator.clipboard.writeText(url);
            toast.success('Dentist link copied to clipboard');
        };

        return (
            <Grid item container alignItems="center" key="nova-3d-viewer">
                <Grid item xs={6}>
                    <Typography variant="body1" weight="bold">
                        Dentist Link
                    </Typography>
                </Grid>
                <Grid item container xs={6} alignItems="center">
                    <Button
                        className={classes.openLinkButton}
                        color="secondary"
                        variant="contained"
                        onClick={handleOpenNova3D}
                    >
                        Open 3D viewer
                    </Button>
                    <Button
                        className={classes.copyLinkButton}
                        color="secondary"
                        variant="outlined"
                        onClick={handleCopyDentistLink}
                    >
                        Copy Dentist Link
                    </Button>
                </Grid>
            </Grid>
        );
    };

    const renderCopyPatientLinkButton = (order) => {
        const handleCopyPatientLink = async () => {
            setCopyingPatientLink(true);

            // The clipboard write must start inside the click gesture (see copyToClipboard), so the
            // request is not awaited here: its pending result is handed over to the clipboard instead.
            const treatmentUrlPromise = apiGet(config.apiName, `/v2/orders/${order.orderId}/patient-link`).then(
                ({ treatmentUrl }) => {
                    if (!treatmentUrl) throw new Error('No URL provided in the response');
                    return treatmentUrl;
                }
            );

            try {
                await copyToClipboard(treatmentUrlPromise);
                toast.success('Patient link copied to clipboard');
            } catch (err) {
                console.error('Error copying patient link:', err);

                // The link may still have been fetched: let the user copy it manually instead of failing
                const treatmentUrl = await treatmentUrlPromise.catch(() => null);
                if (treatmentUrl) {
                    setPatientLinkToCopy(treatmentUrl);
                } else {
                    toast.error('Unable to get the patient link. Please try again.');
                }
            } finally {
                setCopyingPatientLink(false);
            }
        };

        return (
            <Grid item container alignItems="center" key="nova-patient-link">
                <Grid item xs={6}>
                    <Typography variant="body1" weight="bold">
                        Patient Link
                    </Typography>
                    <FormHelperText>(forward to patient - 3D viewer only)</FormHelperText>
                </Grid>
                <Grid item container xs={6} alignItems="center">
                    <Button
                        color="secondary"
                        variant="outlined"
                        onClick={handleCopyPatientLink}
                        disabled={isCopyingPatientLink}
                        startIcon={isCopyingPatientLink ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        Copy Patient Link
                    </Button>
                </Grid>
            </Grid>
        );
    };

    const renderCaseLink = ({ url, type, token }) => {
        const isApprovalLink = type === 'approval';
        return (
            <Grid item container alignItems="center" key={`link-${type}`}>
                <Grid item xs={6}>
                    {isApprovalLink ? (
                        <>
                            <Typography variant="body1" weight="bold">
                                Dentist link
                            </Typography>
                            <FormHelperText>(for case approval)</FormHelperText>
                        </>
                    ) : (
                        <>
                            <Typography variant="body1" weight="bold">
                                Patient link
                            </Typography>
                            <FormHelperText>(forward to patient - 3D viewer only)</FormHelperText>
                        </>
                    )}
                </Grid>
                <Grid item container xs={6} alignItems="center">
                    {isApprovalLink && (
                        <Tooltip
                            placement="top"
                            color="secondary"
                            disableFocusListener
                            disableTouchListener
                            title="Copy to access the case details"
                            enterDelay={500}
                        >
                            <Button
                                className={classes.copyTokenButton}
                                color="primary"
                                variant="contained"
                                onClick={handleCopyToken(token)}
                            >
                                Click to copy token
                            </Button>
                        </Tooltip>
                    )}
                    <Button
                        className={classes.openLinkButton}
                        color="secondary"
                        variant="contained"
                        onClick={handleOpenLink(url)}
                    >
                        Open 3D viewer
                    </Button>
                </Grid>
            </Grid>
        );
    };

    const Footer = () => {
        return (
            <Box display="flex" justifyContent="space-between" style={{ marginTop: 40 }}>
                <Button onClick={backToList} variant="outlined" color="secondary">
                    Back
                </Button>
                <Button onClick={handleClickEdit} variant="contained" color="secondary">
                    Edit
                </Button>
            </Box>
        );
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Widget>
                    <Grid item justifyContent="center" container>
                        <Box display="flex" flexDirection="column" width={720}>
                            <Typography variant="h5" weight="medium" style={{ marginBottom: 30 }}>
                                Order details
                            </Typography>

                            <Grid container direction="column" spacing={3}>
                                {orderState?.findLoading ? (
                                    <CircularProgress color="secondary" className={classes.progressCentered} />
                                ) : (
                                    renderOrderSections(orderState?.currentOrder)
                                )}
                            </Grid>

                            <Footer />
                        </Box>
                    </Grid>
                </Widget>
            </Grid>
            <Dialog
                open={isCancelOrderModalOpen}
                onClose={() => setCancelOrderModalOpen(false)}
                scroll={'body'}
                aria-labelledby="cancel-order-dialog-title"
            >
                <DialogTitle id="cancel-order-dialog-title">
                    Cancel Order #{orderState?.currentOrder?.serialNumber}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="email-change-dialog-description">
                        Canceling this order will permanently stop the production process. The practice will be notified
                        and any design work already completed will not be processed further.
                        <br />
                        <br />
                        <strong>This action cannot be undone.</strong>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleModalAction('Cancel')} color="primary">
                        Keep Order
                    </Button>
                    <Button onClick={handleModalAction('Confirm')} color="primary" variant="contained" autoFocus>
                        Yes, Cancel Order
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={!!patientLinkToCopy} onClose={() => setPatientLinkToCopy(null)} fullWidth maxWidth="sm">
                <DialogTitle>Patient Link</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Your browser blocked the automatic copy. Copy the link below to share it with the patient.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        variant="outlined"
                        value={patientLinkToCopy || ''}
                        InputProps={{ readOnly: true }}
                        inputProps={{ 'aria-label': 'Patient link' }}
                        onFocus={(e) => e.target.select()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPatientLinkToCopy(null)} color="primary">
                        Close
                    </Button>
                    <Button onClick={handleCopyPatientLinkFromDialog} color="primary" variant="contained">
                        Copy Link
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default ViewOrder;
